/* eslint-disable react/jsx-max-depth */
import React from 'react'
import { List } from 'immutable'
import { Errored, Loading, State, hasLoadedWithoutError } from 'rwwa-data-access'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import {
  GiddyUpAcceptors,
  IAcceptor,
  IAcceptorRecord,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpAcceptors'
import {
  GiddyUpRaces,
  IRace,
  IRaceRecord,
  MeetingCode,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpRaces'
import { trackGiddyUpShowTopRating } from '@core/Areas/Racing/Components/RacingForm/helpers/analytics'
import { getAcceptor, isDog } from '@core/Areas/Racing/Components/RacingForm/integration'
import type { SilkImage } from '@core/Areas/Racing/Components/RacingForm/types'
import { observe } from '@core/Components/HOCs/observe'
import { DogColour } from '../../../Common/DogColour'
import { LastUpdated } from '../../../Common/LastUpdated'
import { OddsContext, OddsPrice, OddsToggle, OddsType, guOddsAsString } from '../../../Common/Odds'
import { RatingStar } from '../../../Common/RatingStar'
import { RatingTacho } from '../../../Common/RatingTacho'
import { CardTitleStyled } from '../Common/GiddyUp.styles'

const TopRatingComponent: React.FC<{
  races: State<IRaceRecord>
  acceptors: State<IAcceptorRecord>
  raceKey: string
}> = ({ races, acceptors, raceKey }) => {
  const race = GiddyUpRaces.getFrom(races, raceKey)

  React.useEffect(trackGiddyUpShowTopRating, [])

  if (!hasLoadedWithoutError(race)) {
    return <div />
  }

  return <TopRatingLoaded race={race} acceptors={acceptors} />
}

const state$ = Rx.Observable.combineLatest(
  GiddyUpRaces.data$,
  GiddyUpAcceptors.data$,
  (races, acceptors) => ({ races, acceptors })
)

export const TopRating = observe<
  { races: State<IRaceRecord>; acceptors: State<IAcceptorRecord> },
  { raceKey: string }
>(state$, TopRatingComponent)

// ================
// Local Components
// ================

const TopRatingLoaded: React.FC<{
  race: IRaceRecord
  acceptors: State<IAcceptorRecord>
}> = ({ race, acceptors }) => {
  const [oddsContextValue, setOddsContextValue] = React.useState<OddsType>('fixed')
  const handleToggleOddsContext = () =>
    setOddsContextValue(curr => (curr === 'fixed' ? 'tote' : 'fixed'))

  return (
    <OddsContext.Provider
      value={{ currentOdds: oddsContextValue, toggleOdds: handleToggleOddsContext }}
    >
      <div data-tid-recommended style={{ maxWidth: '64em', margin: '0 auto' }}>
        <div
          className='l-faux-table w100 p1em'
          style={{
            boxShadow: '0 2px 2px -2px rgba(0, 0, 0, 0.4)',
            backgroundColor: '#fff',
            marginBottom: 1,
          }}
        >
          <div className='l-faux-row w100' data-tid-race-confidence>
            <div className='l-faux-cell pb1em l-cell-xs-12'>
              <div style={{ paddingTop: '0.5em' }} className='l-faux-table w100'>
                <div className='l-faux-row w100'>
                  <div
                    className='l-faux-cell l-cell-xs-6'
                    style={{ fontWeight: 'bold', verticalAlign: 'middle' }}
                  >
                    Race Confidence:
                  </div>
                  <div className='l-faux-cell l-cell-xs-5 alignmid text-right pr10'>
                    <RatingStar raceConfidence={race.confidence} />
                  </div>
                  <div
                    className='l-faux-cell l-cell-xs-1'
                    style={{
                      fontWeight: 'bold',
                      verticalAlign: 'middle',
                      color: '#552d6b',
                      fontSize: '1.6em',
                    }}
                  >
                    {`${race.confidence}%`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='l-faux-row' data-tid-race-comment>
            <span
              className='faux-strong db fz-12'
              style={{ borderTop: '1px solid #cecece', paddingTop: '0.5em' }}
            >
              Overview
            </span>
            {race.comment}
          </div>
        </div>

        <div className='l-faux-table w100'>
          <div className='l-faux-row fz-12'>
            <div className='l-faux-cell'>
              <CardTitleStyled className='l-faux-table w100'>
                <div className='l-faux-row fz-12'>
                  <div
                    className='l-faux-cell l-cell-xs-6 alignmid faux-strong'
                    style={{ color: '#552d6b' }}
                  >
                    Top Raters
                  </div>
                  <div className='l-faux-cell l-cell-xs-2 alignmid'>
                    <SvgLazy name='GiddyUpLogo' height='1.4rem' color='#0051A0' />
                  </div>
                  <div className='l-faux-cell l-cell-xs-2 alignmid text-center'>
                    <OddsToggle />
                  </div>
                </div>
              </CardTitleStyled>
            </div>
          </div>

          {race.sortedTopRunnerKeys.toArray().map(starterKey => {
            return (
              starterKey && (
                <div key={starterKey} className='l-faux-row' data-tid-top-starters=''>
                  <AcceptorCard acceptor={GiddyUpAcceptors.getFrom(acceptors, starterKey)} />
                </div>
              )
            )
          })}
        </div>

        <Longshot acceptorState={acceptors} acceptorKey={race.longshotAcceptorKey} />
        <BestValueAndFirstStarters acceptors={acceptors} race={race} />
        <LastUpdated date={race.updatedAt} />
      </div>
    </OddsContext.Provider>
  )
}

const Longshot: React.FC<{
  acceptorState: State<IAcceptorRecord>
  acceptorKey: string
}> = ({ acceptorState, acceptorKey }) => {
  const longShotAcceptor = acceptorKey && GiddyUpAcceptors.getFrom(acceptorState, acceptorKey)
  if (!longShotAcceptor || !hasLoadedWithoutError(longShotAcceptor)) {
    return <div />
  }

  return (
    <div className='l-faux-table w100' data-tid-best-longshot-panel>
      <div className='l-faux-row'>
        <CardTitleStyled className='l-faux-cell l-cell-xs-12 fz-12 pt1em'>
          Best Longshot
        </CardTitleStyled>
      </div>
      <div className='l-faux-row'>
        <div className='l-faux-cell l-cell-xs-12' data-tid-best-longshot>
          <AcceptorCard acceptor={longShotAcceptor} />
        </div>
      </div>
    </div>
  )
}

const BestValueAndFirstStarters: React.FC<{
  acceptors: State<IAcceptorRecord>
  race: IRace
}> = ({ acceptors, race }) => {
  if (race.meetingCode === MeetingCode.Dogs) {
    return <div />
  }

  const acceptorNumbers = race.maidenAcceptorKeys
    .map(key => key && GiddyUpAcceptors.getFrom(acceptors, key))
    .filter(a => !!a)
    .map(a => (a as IAcceptor).acceptorNumber)

  return (
    <div
      className='l-faux-table w100'
      style={{
        padding: '0.6em 1em',
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: '#fff',
      }}
    >
      <div className='l-faux-row'>
        <div className='l-faux-cell l-cell-xs-6'>
          {race.meetingCode === MeetingCode.Races ? (
            <BestValueNumbers acceptors={acceptors} valueAcceptorKeys={race.valueAcceptorKeys} />
          ) : (
            <div>
              <div className='faux-strong'>Early Speed</div>
              <div data-tid-early-speed>{race.earlySpeed ? race.earlySpeed : '-'}</div>
            </div>
          )}
        </div>

        <div className='l-faux-cell l-cell-xs-6 text-right'>
          <div className='faux-strong'>First Starters</div>
          <div data-tid-maiden-starters>
            {acceptorNumbers.size >= 1 ? acceptorNumbers.join(', ') : '-'}
          </div>
        </div>
      </div>
    </div>
  )
}

const BestValueNumbers: React.FC<{
  acceptors: State<IAcceptorRecord>
  valueAcceptorKeys: List<string>
}> = ({ valueAcceptorKeys, acceptors }) => {
  const acceptorNumbers = valueAcceptorKeys
    .map(key => key && GiddyUpAcceptors.getFrom(acceptors, key))
    .filter(a => !!a)
    .map(a => (a as IAcceptor).acceptorNumber)

  return (
    <div>
      <div className='faux-strong'>Best Value No.s</div>
      <div data-tid-value-starters>
        {acceptorNumbers.size >= 1 ? acceptorNumbers.join(', ') : '-'}
      </div>
    </div>
  )
}

const AcceptorCard: React.FC<{ acceptor: IAcceptor | Loading | Errored }> = ({ acceptor }) => {
  if (!hasLoadedWithoutError(acceptor)) {
    return <div />
  }

  if (!acceptor) {
    return <span>No information availabe</span>
  }

  const extraInfo = getAcceptor(acceptor.key, acceptor.acceptorNumber)
  if (!extraInfo) {
    return <span>No information availabe</span>
  }

  return (
    <div
      className='l-faux-table w100'
      style={{
        padding: '0.6em 1em',
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: '#fff',
      }}
      data-tid-acceptor-details
    >
      <div className='l-faux-row'>
        {isDog(extraInfo) ? (
          <div
            className='l-faux-cell l-cell-xs-1 alignmid fz-14 lh1'
            style={{ paddingRight: '0.5em' }}
            data-tid-acceptor-number
          >
            <DogColour acceptorNumber={acceptor.acceptorNumber} />
          </div>
        ) : (
          <AcceptorNumberWithSilk
            silk={extraInfo.silkImage}
            acceptorNumber={acceptor.acceptorNumber}
          />
        )}

        <div
          className={`${isDog(extraInfo) ? 'l-cell-xs-5' : 'l-cell-xs-4'} l-faux-cell alignmid faux-strong lh1`}
          style={{ wordBreak: 'break-word', hyphens: 'auto', paddingRight: '0.5em' }}
          data-tid-acceptor-name
        >
          {' '}
          {extraInfo.name}{' '}
        </div>

        <div className=' l-cell-xs-2 l-faux-cell aligntop text-center fz-12'>
          <span style={{ color: '#004e9d', fontWeight: 'bold' }} data-tid-required-odds>
            {guOddsAsString(acceptor.requiredOdds)}
          </span>{' '}
          <RatingTacho rating={acceptor.rating} />
        </div>

        <div className='l-cell-xs-2 l-faux-cell faux-strong aligntop'>
          <OddsPrice acceptor={extraInfo} />
        </div>
      </div>
    </div>
  )
}

const AcceptorNumberWithSilk: React.FC<{
  silk: SilkImage
  acceptorNumber: number
}> = ({ silk, acceptorNumber }) => {
  const { cssurl } = silk

  return (
    <div className='l-faux-cell l-cell-xs-2 alignmid'>
      <div className='l-faux-table w100'>
        <div className='l-faux-row'>
          <div className='l-faux-cell l-cell-xs-6 alignmid text-left fz-12' data-tid-silk-image>
            <span
              data-tid-silk=''
              style={{
                background: cssurl,
                width: silk.width,
                display: 'inline-block',
                height: '2.5em',
                marginTop: '.0833em',
              }}
            />
          </div>
          <div
            style={{ paddingRight: '0.5em' }}
            className='l-faux-cell l-cell-xs-6 alignmid text-center'
            data-tid-acceptor-number
          >
            {' '}
            {acceptorNumber}
          </div>
        </div>
      </div>
    </div>
  )
}
